import { FC } from 'react';
import { useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import { Stack } from '@mui/system';

import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import CustomerDetailsPanel from '@/shared/components/panels/CustomerDetailsPanel';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import DialogName from '@/shared/types/dialogName.enum';
import Payment from '@/shared/types/order/payment.type';

import GatewayDetailsPanel from './components/GatewayDetailsPanel';
import IssueRefundDialog from './components/IssueRefundDialog';
import PaymentDetailsPanel from './components/PaymentDetailsPanel';
import RefundConfirmationDialog from './components/RefundConfirmationDialog';
import Title from './components/Title';
import usePaymentDetailsData from './hooks/usePaymentDetailsData';
import usePaymentsOperations from './hooks/usePaymentsOperations';

const PaymentDetails: FC = () => {
  const { id } = useParams();
  const transactionId = id || '';

  const { customer, payment, loading, error } = usePaymentDetailsData({
    transactionId: transactionId,
  });

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const { onRefundPayment } = usePaymentsOperations({
    dialog,
    onUpdateDialogState,
    onUpdateNotificationBar,
    onUpdateProgressDialog,
  });

  if (error) throw error;

  return (
    <div>
      <Title
        transactionId={transactionId}
        onRefund={() =>
          onUpdateDialogState(DialogName.IssueRefund, {
            data: payment,
            open: true,
            errors: [],
          })
        }
      />

      {loading && (
        <Stack alignItems='center' justifyContent='center' height='70vh'>
          <CircularProgress />
        </Stack>
      )}

      <Stack
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          alignItems: 'start',
          gap: 2,
          mb: 10,
        }}
      >
        <Stack spacing={2}>
          {customer && (
            <CustomerDetailsPanel customer={customer} isBillingAddressVisible />
          )}
          {payment && <GatewayDetailsPanel payment={payment} />}
        </Stack>

        {payment && <PaymentDetailsPanel payment={payment} />}
      </Stack>

      {dialog?.issueRefund?.open && (
        <IssueRefundDialog
          payment={dialog.issueRefund.data as Payment}
          errors={dialog.issueRefund.errors}
          onClose={() =>
            onUpdateDialogState(DialogName.IssueRefund, { open: false })
          }
          proceed={(payment, amount) => {
            onUpdateDialogState(DialogName.IssueRefundConfirmation, {
              data: {
                transactionId: payment.in_transaction_id,
                orderId: payment.in_order_id,
                amount,
              },
              open: true,
            });
          }}
        />
      )}

      {dialog?.issueRefundConfirmation?.open && (
        <RefundConfirmationDialog
          onClose={() =>
            onUpdateDialogState(DialogName.IssueRefundConfirmation, {
              open: false,
            })
          }
          proceed={() => {
            void onRefundPayment();
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
        onClose={() => onUpdateProgressDialog({ open: false })}
      />
    </div>
  );
};

export default PaymentDetails;
